import { TAction } from '../../../common/types';
import { AudioPlayerStates } from '../../types/common';
import { IAppSettings } from '../../../common/rest/user/userRest';

export const SYNC_USER_LOCAL_STORAGE = 'GENERAL/SYNC_USER_LOCAL_STORAGE';
export const CHANGE_AUTO_SYNC = 'GENERAL/CHANGE_AUTO_SYNC';
export const SET_AUTH_USER = 'GENERAL/SET_AUTH_USER';
export const SET_AUTO_PAUSE_SIZE = 'GENERAL/SET_AUTO_PAUSE_SIZE';
export const SET_HOVER_AUTO_PAUSE = 'GENERAL/SET_HOVER_AUTO_PAUSE';
export const SET_LIBRARY_GROUP = 'GENERAL/SET_LIBRARY_GROUP';
export const SET_AUDIO_PLAYER_STATE = 'GENERAL/SET_AUDIO_PLAYER_STATE';
export const SET_PLAYER_RATE = 'GENERAL/SET_PLAYER_RATE';
export const SET_PLAY_MODE = 'GENERAL/SET_PLAY_MODE';
export const SET_ONE_TIME_TOOLTIP = 'GENERAL/SET_ONE_TIME_TOOLTIP';
export const SET_MAIN_VIEW_MODE = 'GENERAL/SET_MAIN_VIEW_MODE';

export const SET_SHOW_NATIVE_CAPTIONS = 'GENERAL/SET_SHOW_NATIVE_CAPTIONS';

export const SET_SNACKBAR_PANEL = 'GENERAL/SET_SNACKBAR_PANEL';
export const SET_APP_SETTINGS = 'GENERAL/SET_APP_SETTINGS';
export const SET_PAUSE_ON_NOTE = 'GENERAL/SET_PAUSE_ON_NOTE';
export const SET_ROUTE_ON_LOGIN = 'GENERAL/SET_ROUTE_ON_LOGIN';
export const SET_VIDEO_WATCH_TODAY = 'GENERAL/SET_VIDEO_WATCH_TODAY';
export const SET_SHOW_TRANSLATIONS = 'GENERAL/SET_SHOW_TRANSLATIONS';
export const UPDATE_GENERAL = 'GENERAL/UPDATE_GENERAL';

export enum ELanguageStatus {
  KNOW = '1',
  TO_LEARN = '2'
}

export enum ESettingsTabId {
  CREATE = 1,
  GROUPS = 2,
}

export interface ILanguageItem {
  name: string;
  code: string;
  status?: ELanguageStatus;
}

export enum EPlayMode {
  DEFAULT,
  REPLAY
}

export enum EAuthUserRole {
  COMMON = 0, ADMIN = 1
}

export type TAuthUserTranslateLang = {
  code: string,
  orderNum: number
}

export enum EAuthUserConfirmEmailStatus {
  NOT_SET = 0, AWAIT = 1, DONE = 2
}

export type TAuthUser = {
  id: number;
  email: string;
  googleId: string;
  hashId: string;
  name: string;
  picture: string;
  locale: string;
  token: string;
  refreshToken: string;
  isNew: boolean;
  role: EAuthUserRole;
  translateLangs: TAuthUserTranslateLang[];
  roles: number[];
  teacher: boolean;
  teacherMode: boolean;
  level: EUserLevel;
  inviteByUserId?: number;
  confirmEmailStatus?: EAuthUserConfirmEmailStatus;
  langCode?: string;
  anonymous?: boolean;
}

export enum EUserLevel {
  UNKNOWN = 0, BEGINNER = 1, INTERMEDIATE = 2, ADVANCED = 3
}

export enum EUserGroupType {
  USER = 0,
  PREVIEW = 1,
  CONFERENCE = 2
}

export interface ISnackbarPanel {
  show: boolean,
  text: string
}

export interface ISyncUserLocalStorageData {
  autoPause: number;
  showPinExtensionPopup: boolean;
  mainLayoutType: EMainLayoutType;
  showLeftPanel: boolean;
  phrasesColumn: EPhrasesColumnType;
}

export interface IOneTimeTooltips {
  showPhrasesEditTooltip: boolean;
  showNativeLanguageTooltip: boolean;
  showNativeAvailableTooltip: boolean;
  showTutorialPopup: boolean;
  showTutorialTooltip: boolean;
  showSaveForMagicTooltip: boolean;
  showSavedForMagicTooltip: boolean;
  showTranslations: boolean;
  showFullscreen: boolean;
  showFirstGroup?: number;
  showSelectionTip?: boolean;
}

export enum EMainLayoutType {
  COLUMNS_3, COLUMNS_4
}

export enum EPhrasesColumnType {
  COLUMNS_1,
  COLUMNS_2,
  HIDE,
}

export enum EPhrasesColumnTab {
  INTRO = 'INTRO',
  TRANSLATE = 'TRANSLATE',
  TRANSLATED = 'TRANSLATED',
  SAVED = 'SAVED',
  TEACHERS = 'TEACHERS',
  EXERCISES = 'EXERCISES',
  TEACHER_VIDEO_COMMENT_EDITOR = 'TEACHER_VIDEO_COMMENT_EDITOR',
  TEACHER_VIDEO_COMMENT = 'TEACHER_VIDEO_COMMENT'
}

export enum EPhrasesColumnTab0 {
  LEARN = 'LEARN',
  PHRASES = 'PHRASES',
}

export interface IMainLayout {
  showLeftPanel: boolean,
  type: EMainLayoutType
  phrasesColumn: EPhrasesColumnType;
  phrasesColumnTab: EPhrasesColumnTab;
  phrasesColumnTabPrev: EPhrasesColumnTab;
}

export enum EMainViewMode {
  PHRASES,
  MY_LIBRARY,
  CALL,
}

export interface IRouteOnLogin {
  route?: string;
}

export interface IGeneralState {
  autoPause: number;
  autoSync: boolean;
  hoverAutoPause: boolean;
  authUser: TAuthUser | null;
  libraryGroupId: number | null;
  playerRate: number;
  playMode: EPlayMode;
  audioPlayerState: AudioPlayerStates;
  snackbarPanel: ISnackbarPanel;
  oneTimeTooltip: IOneTimeTooltips;
  mainViewMode: EMainViewMode;
  mainLayout: IMainLayout;
  showNativeCaptions: boolean;
  appSettings: IAppSettings;
  pauseOnNote: boolean;
  routeOnLogin: IRouteOnLogin | null;
  videoWatchToday: number | null;
  showTranslations?: boolean;
  showFirstVideoPopup?: boolean;
  useShortcuts?: boolean;
  autoSpeechRate?: number;
  speechLangVoiceMap?: Record<string, string>;
}
export interface ISyncUserLocalStorage extends TAction<typeof SYNC_USER_LOCAL_STORAGE> {
  data: ISyncUserLocalStorageData
}

export interface IChangeAutoSync extends TAction<typeof CHANGE_AUTO_SYNC> {
  state: boolean;
}

export interface ISetAuthUser extends TAction<typeof SET_AUTH_USER> {
  user: TAuthUser | null
}
export interface ISetAutoPause extends TAction<typeof SET_AUTO_PAUSE_SIZE> {
  pause: number
}

export interface ISetHoverAutoPause extends TAction<typeof SET_HOVER_AUTO_PAUSE> {
  hoverAutoPause: boolean
}

export interface ISetLibraryGroupId extends TAction<typeof SET_LIBRARY_GROUP> {
  groupId: number | null
}
export interface ISetAudioPlayerState extends TAction<typeof SET_AUDIO_PLAYER_STATE> {
  state: AudioPlayerStates
}

export interface ISetPlayerRate extends TAction<typeof SET_PLAYER_RATE> {
  rate: number
}
export interface ISetPlayMode extends TAction<typeof SET_PLAY_MODE> {
  mode: EPlayMode
}

export interface ISetOneTimeTooltip extends TAction<typeof SET_ONE_TIME_TOOLTIP> {
  value: Partial<IOneTimeTooltips>
}

export interface ISetMainViewMode extends TAction<typeof SET_MAIN_VIEW_MODE> {
  mainViewMode: EMainViewMode
}

export enum EHintPopupContentType {
  PIN
}

export interface ISetShowNativeCaptions extends TAction<typeof SET_SHOW_NATIVE_CAPTIONS> {
  show: boolean
}

export interface ISetSnackbarPanelAction {
  type: typeof SET_SNACKBAR_PANEL,
  show: boolean,
  text: string
}

export interface ISetAppSettingsAction {
  type: typeof SET_APP_SETTINGS,
  value: Partial<IAppSettings>
}

export interface ISetPauseOnNoteAction {
  type: typeof SET_PAUSE_ON_NOTE,
  pauseOnNote: boolean
}

export interface ISetRouteOnLoginAction extends TAction<typeof SET_ROUTE_ON_LOGIN> {
  data: Partial<IRouteOnLogin>
}

export interface ISetVideoWatchTodayAction extends TAction<typeof SET_VIDEO_WATCH_TODAY> {
  videoWatchToday: number
}

export interface ISetShowTranslations extends TAction<typeof SET_SHOW_TRANSLATIONS> {
  value: boolean
}

export interface IUpdateGeneral extends TAction<typeof UPDATE_GENERAL> {
  value: Partial<IGeneralState>
}

export type IGeneralActions =
  ISyncUserLocalStorage
    | IChangeAutoSync
    | ISetAuthUser
    | ISetAutoPause
    | ISetHoverAutoPause
    | ISetLibraryGroupId
    | ISetAudioPlayerState
    | ISetPlayerRate
    | ISetPlayMode
    | ISetOneTimeTooltip
    | ISetMainViewMode
    | ISetShowNativeCaptions
    | ISetSnackbarPanelAction
    | ISetAppSettingsAction
    | ISetPauseOnNoteAction
    | ISetRouteOnLoginAction
    | ISetVideoWatchTodayAction
    | ISetShowTranslations
    | IUpdateGeneral
